/**
 * Colonnes de la table Résultats — entretien structuré (plan v3, phase 7.1).
 *
 * Remplace la table maison de `CampagneResultatsTab` : chevrons de tri, popover
 * « Colonnes » et `<colgroup>` conditionnel sont désormais portés par le
 * `DataTable`.
 *
 * Particularité de cette table : ses colonnes sont **dynamiques**, une par
 * compétence du barème du template. Elles sont donc construites par une
 * fabrique, à mémoïser par l'appelant — une nouvelle référence à chaque rendu
 * ferait remonter la table entière.
 */
import { Badge, Button, type ColonneDataTable } from "@repo/ui";
import { dateCourte } from "@repo/utils";
import { AlertTriangle } from "lucide-react";
import type { EvaluationWithScores } from "./CampagneResultatsTab";

function getScoreColor(percentage: number): string {
  if (percentage >= 61) return "bg-etat-fait-surface text-etat-fait-contenu";
  if (percentage >= 21) return "bg-etat-alerte-surface text-etat-alerte-contenu";
  return "bg-etat-erreur-surface text-etat-erreur-contenu";
}

/** Score d'une compétence pour une ligne, en pourcentage et en brut. */
function scoreCompetence(ligne: EvaluationWithScores, nom: string) {
  const comp = ligne.scores?.competences.find((c) => c.nom === nom);
  const obtenu = comp?.scoreObtenu ?? 0;
  const max = comp?.scoreMax ?? 0;
  return {
    obtenu,
    max,
    pourcentage: max > 0 ? Math.round((obtenu / max) * 100) : 0,
  };
}

/** Id de colonne d'une compétence : dérivé du nom, donc stable d'un chargement
 * à l'autre — c'est lui qui sert de clé à la visibilité persistée. */
export function idCompetence(nom: string) {
  return `comp:${nom}`;
}

export function colonnesResultats(options: {
  /** Compétences du barème, une colonne chacune. */
  competences: { nom: string }[];
  /** Libellé du bouton d'action (défaut : « Voir détails »). */
  libelleAction?: string;
}): ColonneDataTable<EvaluationWithScores>[] {
  const { competences, libelleAction = "Voir détails" } = options;

  return [
    {
      // Prénom et nom fusionnés, comme sur la table des parcours : deux
      // bascules pour un seul concept, c'était deux fois trop.
      id: "candidat",
      header: "Candidat",
      accessorFn: (l) =>
        `${l.prenomCandidat ?? ""} ${l.nomCandidat ?? ""}`.trim(),
      cell: ({ row }) => (
        <span className="font-medium">
          {`${row.original.prenomCandidat ?? ""} ${row.original.nomCandidat ?? ""}`.trim() ||
            "—"}
        </span>
      ),
      enableHiding: false,
      meta: { libelle: "Candidat", largeur: "18%" },
    },
    {
      id: "date",
      header: "Date",
      accessorFn: (l) => l.dateFin ?? 0,
      cell: ({ row }) => (
        <span className="data">
          {row.original.dateFin ? dateCourte(row.original.dateFin) : "—"}
        </span>
      ),
      meta: { libelle: "Date", largeur: "10%" },
    },
    {
      id: "suspect",
      header: "",
      enableSorting: false,
      accessorFn: (l) => (l.isSuspect ? 1 : 0),
      cell: ({ row }) =>
        row.original.isSuspect ? (
          <AlertTriangle
            className="h-4 w-4 text-etat-alerte"
            aria-label="Comportement suspect détecté (copier-coller ou changement d'onglet)"
          />
        ) : null,
      meta: { libelle: "Alerte", largeur: "5%" },
    },
    ...competences.map<ColonneDataTable<EvaluationWithScores>>((comp) => ({
      id: idCompetence(comp.nom),
      header: () => (
        // Pas de `Tooltip` ici : l'en-tête triable est déjà un `<button>`, un
        // déclencheur imbriqué produirait un bouton dans un bouton.
        <span className="text-left break-words hyphens-auto" title={comp.nom}>
          {comp.nom}
        </span>
      ),
      // Tri sur le score brut, comme la table maison le faisait.
      accessorFn: (l) => scoreCompetence(l, comp.nom).obtenu,
      cell: ({ row }) => {
        const { obtenu, max, pourcentage } = scoreCompetence(
          row.original,
          comp.nom,
        );
        return (
          <Badge
            className={`${getScoreColor(pourcentage)} pointer-events-none`}
          >
            {obtenu}/{max}
          </Badge>
        );
      },
      meta: { libelle: comp.nom, largeur: "10%" },
    })),
    {
      id: "scoreGlobal",
      header: "Score global",
      accessorFn: (l) => l.globalPercentage,
      cell: ({ row }) => (
        <Badge
          className={`${getScoreColor(row.original.globalPercentage)} pointer-events-none`}
        >
          {row.original.globalPercentage}%
        </Badge>
      ),
      meta: { libelle: "Score global", largeur: "10%" },
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      enableHiding: false,
      // La ligne entière est déjà un lien : ce bouton n'est qu'un affordance
      // visuel, retiré du parcours de tabulation pour ne pas doubler la cible.
      cell: () => (
        <Button variant="ghost" size="sm" tabIndex={-1} aria-hidden>
          {libelleAction}
        </Button>
      ),
      meta: { largeur: "10%", aDroite: true },
    },
  ];
}
